import { create } from 'zustand'
import {
  getCampaigns,
  getCampaign,
  createCampaign,
  updateCampaign,
  deleteCampaign as deleteCampaignQuery,
  getNewsletterRecipientCount,
} from '@unvld/db'
import type { Campaign } from '@unvld/db'

interface CampaignsState {
  campaigns: Campaign[]
  currentCampaign: Campaign | null
  loading: boolean
  error: string | null
  recipientCount: number | null
  recipientCountLoading: boolean

  fetchCampaigns: (filters?: { status?: string }) => Promise<void>
  fetchCampaign: (id: string) => Promise<void>
  createCampaign: (data: {
    subject: string
    body_html: string
    segment_filter?: { tags?: string[]; min_visits?: number } | null
  }) => Promise<Campaign>
  updateCampaign: (id: string, updates: Partial<Campaign>) => Promise<Campaign>
  deleteCampaign: (id: string) => Promise<void>
  fetchRecipientCount: (segmentFilter?: {
    tags?: string[]
    min_visits?: number
  }) => Promise<void>
  clearCurrent: () => void
}

export const useCampaigns = create<CampaignsState>((set) => ({
  campaigns: [],
  currentCampaign: null,
  loading: false,
  error: null,
  recipientCount: null,
  recipientCountLoading: false,

  fetchCampaigns: async (filters) => {
    set({ loading: true, error: null })
    try {
      const campaigns = await getCampaigns(filters)
      set({ campaigns, loading: false })
    } catch (err: any) {
      set({ loading: false, error: err.message })
    }
  },

  fetchCampaign: async (id) => {
    set({ loading: true, error: null })
    try {
      const campaign = await getCampaign(id)
      set({ currentCampaign: campaign, loading: false })
    } catch (err: any) {
      set({ loading: false, error: err.message })
    }
  },

  createCampaign: async (data) => {
    set({ loading: true, error: null })
    try {
      const campaign = await createCampaign(data)
      set((state) => ({
        campaigns: [campaign, ...state.campaigns],
        loading: false,
      }))
      return campaign
    } catch (err: any) {
      set({ loading: false, error: err.message })
      throw err
    }
  },

  updateCampaign: async (id, updates) => {
    set({ loading: true, error: null })
    try {
      const updated = await updateCampaign(id, updates)
      set((state) => ({
        campaigns: state.campaigns.map((c) => (c.id === id ? updated : c)),
        currentCampaign: state.currentCampaign?.id === id ? updated : state.currentCampaign,
        loading: false,
      }))
      return updated
    } catch (err: any) {
      set({ loading: false, error: err.message })
      throw err
    }
  },

  deleteCampaign: async (id) => {
    try {
      await deleteCampaignQuery(id)
      set((state) => ({
        campaigns: state.campaigns.filter((c) => c.id !== id),
        currentCampaign: state.currentCampaign?.id === id ? null : state.currentCampaign,
      }))
    } catch (err: any) {
      set({ error: err.message })
      throw err
    }
  },

  fetchRecipientCount: async (segmentFilter) => {
    set({ recipientCountLoading: true })
    try {
      const count = await getNewsletterRecipientCount(segmentFilter)
      set({ recipientCount: count, recipientCountLoading: false })
    } catch (err: any) {
      set({ recipientCountLoading: false, error: err.message })
    }
  },

  clearCurrent: () => set({ currentCampaign: null }),
}))
